import React, { useImperativeHandle, forwardRef, useState, useEffect, useRef } from 'react';
import { Box, Typography, Alert, useMediaQuery, Button } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { getThemeStyles } from '../../../themes/themeConfig';
import OptimizedInput from '../../input/OptimizedInput';
import { useTranslation } from 'react-i18next';

// Importación condicional de íconos (si están disponibles)
let AtomIcon, LightbulbIcon;
try {
  // Agregar solo los íconos de Lucide React que necesitamos
  const lucideIcons = require('lucide-react');
  AtomIcon = lucideIcons.Atom;
  LightbulbIcon = lucideIcons.Lightbulb;
} catch (e) {
  // Si no se pueden importar, usar un componente vacío
  const EmptyIcon = () => null;
  AtomIcon = EmptyIcon;
  LightbulbIcon = EmptyIcon;
}

// Estilos
const MessageBarContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isRecording'
})(({ theme, isRecording }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    padding: '0px 18px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'sticky',
    bottom: 0,
    width: '100%',
    maxWidth: '800px',
    margin: '0 auto',
    backgroundColor: isRecording
      ? 'transparent' // Completely transparent when recording
      : isWallpaperMode
        ? 'transparent' // Transparent in wallpaper mode to show wallpaper
        : theme.palette.mode === 'dark' ? '#1C1C1E' : '#F2F2F7',
    transition: 'background-color 0.3s ease',
    ...getThemeStyles('MessageBarContainer', theme.palette.mode),
  };
});

// Contenedor wrapper simplificado solo para las alertas/labels
const InputWrapper = styled(Box)({
  position: 'relative',
  width: '100%',
});

// Alerta de Ultra minimizada con diseño mejorado y responsive - ACTUALIZADA para coincidir con MinimizedTipAlert
const MinimizedUltraAlert = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    position: 'absolute',
    top: '-31px',
    left: '32px',
    zIndex: 10,
    padding: '6px 14px',
    borderRadius: '12px 12px 0 0',
    fontSize: '12px',
    fontWeight: 600,
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : theme.palette.mode === 'dark'
        ? 'rgba(18, 18, 18, 0.9)'
        : '#FFFFFF',
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    // Sombra más sutil y elegante
    boxShadow: isWallpaperMode
      ? 'none'
      : theme.palette.mode === 'dark'
        ? '0px -1px 3px rgba(105, 191, 249, 0.08), 1px 0px 3px rgba(185, 106, 243, 0.08), -1px 0px 3px rgba(242, 172, 62, 0.08)'
        : '0px -1px 3px rgba(0, 0, 0, 0.04), 1px 0px 3px rgba(0, 0, 0, 0.04), -1px 0px 3px rgba(0, 0, 0, 0.04)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    border: isWallpaperMode
      ? `1px solid rgba(128, 128, 128, 0.3)`
      : `1px solid ${theme.palette.mode === 'dark'
        ? 'rgba(105, 191, 249, 0.15)'
        : 'rgba(0, 0, 0, 0.06)'}`,
    borderBottom: 'none',
    // Texto blanco en wallpaper, gradiente en otros modos
    color: isWallpaperMode ? '#FFFFFF' : 'transparent',
    ...(isWallpaperMode ? {} : {
      background: 'linear-gradient(90deg, #69BFF9 0%, #B96AF3 35%, #E9685E 58%, #F2AC3E 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      textFillColor: 'transparent',
    }),

    '& svg': {
      fontSize: '16px',
      marginRight: '2px',
      color: isWallpaperMode ? '#FFFFFF' : '#84A2F7',
    },
 
    // Adaptaciones para pantallas pequeñas
    [theme.breakpoints.down('sm')]: {
      left: '31px',
      top: '-29px',
      maxWidth: 'calc(100% - 32px)',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      padding: '6px 10px',
      fontSize: '11px',
      '& svg': {
        fontSize: '14px',
      }
    }
  };
});

// Componente para el tip de crear nuevo chat - Ahora con responsive design
const MinimizedTipAlert = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    position: 'absolute',
    top: '-31px',
    left: '32px',
    zIndex: 10,
    padding: '6px 14px',
    borderRadius: '12px 12px 0 0',
    fontSize: '12px',
    fontWeight: 600,
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : theme.palette.mode === 'dark'
        ? 'rgba(18, 18, 18, 0.9)' // Fondo oscuro para el modo oscuro
        : '#FFFFFF',
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    // Sombra más sutil y elegante
    boxShadow: isWallpaperMode
      ? 'none'
      : theme.palette.mode === 'dark'
        ? '0px -1px 3px rgba(105, 191, 249, 0.08), 1px 0px 3px rgba(185, 106, 243, 0.08), -1px 0px 3px rgba(242, 172, 62, 0.08)'
        : '0px -1px 3px rgba(0, 0, 0, 0.04), 1px 0px 3px rgba(0, 0, 0, 0.04), -1px 0px 3px rgba(0, 0, 0, 0.04)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    border: isWallpaperMode
      ? `1px solid rgba(128, 128, 128, 0.3)`
      : `1px solid ${theme.palette.mode === 'dark'
        ? 'rgba(105, 191, 249, 0.15)' // Borde más sutil para modo oscuro
        : 'rgba(0, 0, 0, 0.06)'}`,
    borderBottom: 'none',
    // Texto blanco en wallpaper, gradiente en otros modos
    color: isWallpaperMode ? '#FFFFFF' : 'transparent',
    ...(isWallpaperMode ? {} : {
      background: 'linear-gradient(90deg, #69BFF9 0%, #B96AF3 35%, #E9685E 58%, #F2AC3E 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      textFillColor: 'transparent',
    }),

    '& svg': {
      fontSize: '16px',
      marginRight: '2px',
      color: isWallpaperMode ? '#FFFFFF' : '#84A2F7',
    },

    // Adaptaciones para pantallas pequeñas
    [theme.breakpoints.down('sm')]: {
      left: '31px',
      top: '-29px',
      maxWidth: 'calc(100% - 32px)',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      padding: '6px 10px',
      fontSize: '11px',
      '& svg': {
        fontSize: '14px',
      }
    }
  };
});

// NUEVO: Base Alert Style común para todas las alertas
const AlertBase = styled(Alert)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : theme.palette.mode === 'dark'
        ? '#000000'
        : '#FFFFFF',
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    color: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.7)'
      : theme.palette.mode === 'dark' ? '#fff' : '#000',
    position: 'relative',
    border: 'none',
    borderRadius: '12px',
    padding: theme.spacing(2),
    width: '100%',
    marginBottom: '12px',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: -1,
    borderRadius: '13px',
    padding: '1px',
    background: 'linear-gradient(90deg, #69BFF9, #B96AF3, #E9685E, #F2AC3E, #69BFF9)',
    backgroundSize: '500% 100%',
    animation: 'moveGradient 8s linear infinite',
    zIndex: 0,
    WebkitMask: 
      'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    mask: 
      'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    maskComposite: 'exclude',
  },
  
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: -4,
    borderRadius: '16px',
    background: 'linear-gradient(90deg, #69BFF9, #B96AF3, #E9685E, #F2AC3E, #69BFF9)',
    backgroundSize: '500% 100%',
    animation: 'moveGradient 8s linear infinite',
    zIndex: -1,
    filter: 'blur(6px)',
    opacity: theme.palette.mode === 'dark' ? 0.15 : 0.1,
  },
  
  '@keyframes moveGradient': {
    '0%': { backgroundPosition: '0% 50%' },
    '100%': { backgroundPosition: '100% 50%' }
  },
  
    '& > *': {
      position: 'relative',
      zIndex: 1
    },

    '& .MuiAlert-message': {
      width: '100%'
    }
  };
});

// NUEVO: Ahora todas las alertas usan el mismo estilo base
const RateLimitAlert = styled(AlertBase)({});
const UsageLimitAlert = styled(AlertBase)({});
const MessageTooLongAlert = styled(AlertBase)({});
const ModelSuggestionAlert = styled(AlertBase)({});
const OptimizationTipAlert = styled(AlertBase)({});
const UltraModelAlert = styled(AlertBase)({});

// NUEVO: Botón de gradiente para acciones de upgrade
const GradientButton = styled(Button)(({ theme }) => ({
  borderRadius: '50px',
  padding: '8px 20px',
  fontWeight: 600,
  fontSize: '14px',
  textTransform: 'none',
  background: 'linear-gradient(90deg, #FF8A8A, #FFC58F)',
  color: '#4A2B41',
  border: 'none',
  boxShadow: '0 2px 8px rgba(255, 138, 138, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(90deg, #FF7070, #FFBB7A)',
    boxShadow: '0 4px 12px rgba(255, 138, 138, 0.4)',
  }
}));

const AlertTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '14px',
  marginBottom: '4px'
}));

const AlertDescription = styled(Typography)(({ theme }) => ({
  fontSize: '13px',
  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)'
}));

const AlertDetail = styled(Typography)(({ theme }) => ({
  fontSize: '13px',
  fontWeight: 500,
  marginTop: '4px'
}));

// Variable para almacenar el último ID de rate limit visto
const getLastRateLimitId = () => {
  try {
    return sessionStorage.getItem('lastRateLimitId') || '';
  } catch (e) {
    return '';
  }
};

const setLastRateLimitId = (id) => {
  try {
    sessionStorage.setItem('lastRateLimitId', id);
  } catch (e) {
    console.error('Error saving rate limit ID to sessionStorage:', e);
  }
};

/**
 * ChatInput component - Input area for sending messages
 */
const ChatInput = forwardRef((
  {
    handleSendMessage,
    isWaitingForResponse,
    isStreaming,
    onStopStreaming,
    rateLimitInfo,
    onOpenAGTPromo,
    selectedModel,
    onModelChange,
    selectedPersonality,
    onPersonalityChange,
    isPro,
    userId,
    chatId,
    error,
    onErrorClose,
    lastAssistantMessage,
    onPromptAction,
    onProjectClick,
    context,
    onCreateNewChat,
    onUltraAlertStateChange
  },
  ref
) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Estado para errores
  const [localError, setLocalError] = useState(null);
  
  // Estado para grabación de audio
  const [isAudioRecording, setIsAudioRecording] = useState(false);
  
  // Estado para consejos de optimización
  const [optimizationTip, setOptimizationTip] = useState({
    show: false,
    message: '',
    action: ''
  });
  
  // Estado para sugerencias de modelo
  const [modelSuggestion, setModelSuggestion] = useState(null);
  
  // Deprecated: Ultra model removed
  const isUltraModelSelected = false;
  
  // Estado para el aviso Ultra minimizado
  const [showUltraAlert, setShowUltraAlert] = useState(true);
  const [isUltraAlertMinimized, setIsUltraAlertMinimized] = useState(false);
  
  // Estado para mostrar el tip de nuevo chat
  const [showNewChatTip, setShowNewChatTip] = useState(false);
  
  // Estado para rastrear si el usuario ha enviado un mensaje ignorando la alerta
  const hasIgnoredAlert = useRef(false);
  
  // Referencia al componente de entrada
  const optimizedInputRef = React.useRef(null);
  
  // Referencia para almacenar el último rateLimitInfo.id visto
  const lastRateLimitIdRef = useRef(getLastRateLimitId());
  
  // NUEVO: Estilo para títulos con gradiente
  const gradientTitleStyle = {
    background: 'linear-gradient(90deg, #69BFF9 0%, #B96AF3 35%, #E9685E 58%, #F2AC3E 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    color: 'transparent'
  };
  
  // NUEVO: Estilo para texto basado en tema
  const textColorStyle = {
    color: theme.palette.mode === 'dark' ? '#fff' : '#000'
  };
  
  // Restablecer la minimización cuando cambia el modelo
  useEffect(() => {
    if (isUltraModelSelected) {
      setShowUltraAlert(true);
      // Solo mostrar expandido si el usuario no lo ha minimizado antes
      if (!hasIgnoredAlert.current) {
        setIsUltraAlertMinimized(false);
      }
    }
  }, [isUltraModelSelected]);
  
  // Actualizar la referencia del ID de rate limit cuando cambia
  useEffect(() => {
    if (rateLimitInfo) {
      // Si el rateLimitInfo tiene un ID y es diferente al último visto, es un nuevo rate limit
      const currentRateLimitId = rateLimitInfo.id || 'default';
      
      if (currentRateLimitId !== lastRateLimitIdRef.current) {
        // Es un nuevo rate limit, actualizar el ID
        lastRateLimitIdRef.current = currentRateLimitId;
        setLastRateLimitId(currentRateLimitId);
      }
    }
  }, [rateLimitInfo]);
  
  // Notify parent component about Ultra alert state changes
  useEffect(() => {
    if (isMobile && typeof onUltraAlertStateChange === 'function') {
      onUltraAlertStateChange(isUltraModelSelected && showUltraAlert && !isUltraAlertMinimized);
    }
  }, [isUltraModelSelected, showUltraAlert, isUltraAlertMinimized, isMobile, onUltraAlertStateChange]);
  
  // Verificar el número de mensajes para mostrar el tip de nuevo chat
  useEffect(() => {
    if (context && Array.isArray(context.messages)) {
      const totalMessageCount = context.messages.length;
      
      // Solo mostrar el tip cuando:
      // 1. El chat tiene suficientes mensajes (8+)
      // 2. NO está usando el modelo Ultra (que ya tiene su propia alerta)
      // 3. NO hay un rate limit activo (que tiene prioridad)
      const shouldShow =
        totalMessageCount >= 8 &&
        !isUltraModelSelected &&
        !rateLimitInfo;

      setShowNewChatTip(shouldShow);
    } else {
      setShowNewChatTip(false);
    }
  }, [context, isUltraModelSelected, rateLimitInfo]);
  
  // Observar cambios en chatId para detectar cuando se crea un nuevo chat
  useEffect(() => {
    // Cuando cambia el chatId, significa que estamos en un nuevo chat
    // Asegurarse de que el tip esté oculto inicialmente
    setShowNewChatTip(false);
  }, [chatId]);
  
  // Función para manejar el clic en el tip (crear nuevo chat)
  const handleTipClick = () => {
    if (onCreateNewChat) {
      onCreateNewChat();
      setShowNewChatTip(false);
    } else if (onProjectClick) {
      onProjectClick();
      setShowNewChatTip(false);
    }
  };
  
  // Minimizar alerta de Ultra cuando se envía un mensaje
  const checkAndSendMessage = (message) => {
    // Si el usuario envía un mensaje con el modelo Ultra y la alerta está visible,
    // minimizar la alerta
    if (isUltraModelSelected && showUltraAlert && !isUltraAlertMinimized) {
      setIsUltraAlertMinimized(true);
      hasIgnoredAlert.current = true;
      
      // Notify parent component about the change
      if (isMobile && typeof onUltraAlertStateChange === 'function') {
        onUltraAlertStateChange(false);
      }
    }
    
    // Estimar tamaño del mensaje
    const estimatedSize = message.length;
    
    // Si el mensaje es extremadamente largo
    if (estimatedSize > (isPro ? 25000 : 12000)) {
      setLocalError({
        type: 'message_too_long',
        message: isPro 
          ? t('chat.input.messageTooLong.proMessage')
          : t('chat.input.messageTooLong.freeMessage')
      });
      return;
    }

    handleSendMessage(message);
  };
  
  // Manejar cierre de la alerta Ultra
  const handleUltraAlertClose = () => {
    setIsUltraAlertMinimized(true);
    hasIgnoredAlert.current = true;
    
    // Notify parent component
    if (isMobile && typeof onUltraAlertStateChange === 'function') {
      onUltraAlertStateChange(false);
    }
  };
  
  // Manejar clic en la alerta minimizada
  const handleMinimizedAlertClick = () => {
    setIsUltraAlertMinimized(false);
    
    // Notify parent component
    if (isMobile && typeof onUltraAlertStateChange === 'function') {
      onUltraAlertStateChange(true);
    }
  };
  
  // Extraer advertencia de tokens del último mensaje
  const [tokenWarning, setTokenWarning] = useState(null);
  useEffect(() => {
    if (lastAssistantMessage && lastAssistantMessage._tokenWarning) {
      setTokenWarning(lastAssistantMessage._tokenWarning);
    } else {
      setTokenWarning(null);
    }
    
    if (lastAssistantMessage && lastAssistantMessage._modelSuggestion) {
      setModelSuggestion(lastAssistantMessage._modelSuggestion);
    } else {
      setModelSuggestion(null);
    }
  }, [lastAssistantMessage]);
  
  // Verificar si el chat tiene muchos mensajes y sugerir uno nuevo
  useEffect(() => {
    if (context && context.messages && context.messages.length > 40 && !localStorage.getItem('newChatSuggested')) {
      setOptimizationTip({
        show: true,
        message: t('chat.input.optimizationTip.message'),
        action: t('chat.input.optimizationTip.action')
      });
      // Guardar en localStorage para no mostrar constantemente
      localStorage.setItem('newChatSuggested', 'true');
      // Reiniciar después de 24 horas
      setTimeout(() => localStorage.removeItem('newChatSuggested'), 86400000);
    }
  }, [context, t]);
  
  // Manejar la creación de un nuevo chat
  const handleNewChat = () => {
    setOptimizationTip({...optimizationTip, show: false});
    if (onCreateNewChat) {
      onCreateNewChat();
    }
  };
  
  const combinedError = error || localError;
  
  // Manejar cierre de error local
  const handleLocalErrorClose = () => {
    setLocalError(null);
    if (error && onErrorClose) {
      onErrorClose();
    }
  };
  
  // Exponer métodos de OptimizedInput a través de ref
  useImperativeHandle(ref, () => ({
    focus: () => optimizedInputRef.current?.focus(),
    handlePromptAction: (action) => optimizedInputRef.current?.handlePromptAction(action),
    getTextContent: () => optimizedInputRef.current?.getTextContent(),
    clearContent: () => optimizedInputRef.current?.clearContent(),
    checkAndSendMessage
  }));

  // Verificar si OptimizedInput.UpgradeButton existe
  const hasUpgradeButton = typeof OptimizedInput.UpgradeButton !== 'undefined';
  const UpgradeButtonComponent = hasUpgradeButton ? OptimizedInput.UpgradeButton : GradientButton;
  // Función para determinar qué alerta tiene prioridad
  const determineActiveAlert = () => {
    // Si hay un rate limit activo, tiene la máxima prioridad
    if (rateLimitInfo) {
      return 'rateLimit';
    }

    // Si el modelo Ultra está seleccionado, mostrar su alerta correspondiente
    if (isUltraModelSelected && showUltraAlert) {
      return isUltraAlertMinimized ? 'ultraMinimized' : 'ultra';
    }
    
    // Si hay suficientes mensajes y no usa Ultra, mostrar tip de nuevo chat
    if (showNewChatTip) {
      return 'newChatTip';
    }
    
    // Sin alerta prioritaria
    return null;
  };
  
  const activeAlert = determineActiveAlert();

  return (
    <MessageBarContainer isRecording={isAudioRecording}>
      {/* Advertencia para el uso del modelo AGT Ultra */}
      {isUltraModelSelected && showUltraAlert && !isUltraAlertMinimized && activeAlert === 'ultra' && (
        <UltraModelAlert 
          severity="info"
          icon={false}
          onClose={handleUltraAlertClose}
        >
          <Box>
            <AlertTitle variant="body1" sx={gradientTitleStyle}>
              {t('chat.input.ultraModel.title')}
            </AlertTitle>
            <AlertDescription variant="body2" sx={textColorStyle}>
              {t('chat.input.ultraModel.description')}
            </AlertDescription>
          </Box>
        </UltraModelAlert>
      )}
      
      {/* Consejo de optimización para chats con muchos mensajes */}
      {optimizationTip.show && (
        <OptimizationTipAlert 
          severity="info"
          icon={false}
          onClose={() => setOptimizationTip({...optimizationTip, show: false})}
        >
          <Box>
            <AlertTitle variant="body1" sx={gradientTitleStyle}>
              {t('chat.input.optimizationTip.title')}
            </AlertTitle>
            <AlertDescription variant="body2" sx={textColorStyle}>
              {optimizationTip.message}
            </AlertDescription>
            <Box mt={1.5}>
              <GradientButton onClick={handleNewChat}>
                {optimizationTip.action}
              </GradientButton>
            </Box>
          </Box>
        </OptimizationTipAlert>
      )}
      
      {/* Advertencia de límite de uso del seguimiento de tokens */}
      {tokenWarning && tokenWarning.warning && (
        <UsageLimitAlert 
          severity={tokenWarning.level === 'high' ? "warning" : "info"}
          icon={false}
          onClose={() => setTokenWarning(null)}
        >
          <Box>
            <AlertTitle variant="body1" sx={gradientTitleStyle}>
              {tokenWarning.level === 'high' 
                ? t('chat.input.usageLimit.highTitle') 
                : t('chat.input.usageLimit.normalTitle')}
            </AlertTitle>
            <AlertDescription variant="body2" sx={textColorStyle}>
              {tokenWarning.message}
            </AlertDescription>
            <AlertDetail variant="body2" sx={textColorStyle}>
              {t('chat.input.usageLimit.currentUsage', { percentage: tokenWarning.percentage })}
            </AlertDetail>
            {!isPro && tokenWarning.level === 'high' && (
              <Box mt={1.5}>
                <UpgradeButtonComponent 
                  onClick={onOpenAGTPromo}
                  fullWidth={isMobile}
                >
                  {t('chat.input.modelSuggestion.upgrade')}
                </UpgradeButtonComponent>
              </Box>
            )}
          </Box>
        </UsageLimitAlert>
      )}
      
      {/* Sugerencia de modelo basada en consultas complejas */}
      {!isPro && modelSuggestion && modelSuggestion.suggest && (
        <ModelSuggestionAlert 
          severity="info"
          icon={false}
          onClose={() => setModelSuggestion(null)}
        >
          <Box>
            <AlertTitle variant="body1" sx={gradientTitleStyle}>
              {t('chat.input.modelSuggestion.title')}
            </AlertTitle>
            <AlertDescription variant="body2" sx={textColorStyle}>
              {modelSuggestion.message}
            </AlertDescription>
            <Box mt={1.5}>
              <UpgradeButtonComponent 
                onClick={onOpenAGTPromo}
                fullWidth={isMobile}
              >
                {t('chat.input.modelSuggestion.upgrade')}
              </UpgradeButtonComponent>
            </Box>
          </Box>
        </ModelSuggestionAlert>
      )}
      
      {/* Límite de velocidad diario excedido - NO PUEDE CERRARSE MANUALMENTE */}
      {rateLimitInfo && (
        <RateLimitAlert 
          severity="info"
          icon={false}
          // Eliminamos completamente el onClose para que no se muestre el botón de cierre
        >
          <Box>
            <AlertTitle variant="body1" sx={gradientTitleStyle}>
              {isPro 
                ? t('chat.input.rateLimit.proTitle') 
                : t('chat.input.rateLimit.freeTitle')}
            </AlertTitle>
            <AlertDescription variant="body2" sx={textColorStyle}>
              {rateLimitInfo.message || 
                (isPro 
                  ? t('chat.input.rateLimit.proMessage')
                  : t('chat.input.rateLimit.freeMessage'))}
            </AlertDescription>
            {!isPro && rateLimitInfo && (
              <Box mt={1.5}>
                <UpgradeButtonComponent
                  onClick={onOpenAGTPromo}
                  fullWidth={isMobile}
                >
                  {t('chat.input.modelSuggestion.upgrade')}
                </UpgradeButtonComponent>
              </Box>
            )}
          </Box>
        </RateLimitAlert>
      )}
      
      {/* Error para mensaje demasiado largo u otros errores */}
      {combinedError && (
        <MessageTooLongAlert 
          severity="error" 
          icon={false}
          onClose={handleLocalErrorClose}
        >
          <Box>
            <AlertTitle variant="body1" sx={gradientTitleStyle}>
              {combinedError.type === 'message_too_long' 
                ? t('chat.input.messageTooLong.title') 
                : t('chat.input.error.title')}
            </AlertTitle>
            <AlertDescription variant="body2" sx={textColorStyle}>
              {combinedError.message || t('chat.input.error.generic')}
            </AlertDescription>
            {!isPro && combinedError.type === 'message_too_long' && (
              <Box mt={1.5}>
                <UpgradeButtonComponent 
                  onClick={onOpenAGTPromo}
                  fullWidth={isMobile}
                >
                  {t('chat.input.modelSuggestion.upgrade')}
                </UpgradeButtonComponent>
              </Box>
            )}
          </Box>
        </MessageTooLongAlert>
      )}
      
      {/* Wrapper para alertas/labels y el input */}
      <InputWrapper>
        {/* Alertas minimizadas (pegadas al input) - Solo mostrar UNA según la prioridad */}
        {activeAlert === 'ultraMinimized' && (
          <MinimizedUltraAlert onClick={handleMinimizedAlertClick}>
            {AtomIcon && <AtomIcon size={16} style={{ color: '#BA6AEE' }} />}
            {t('chat.input.labels.agtUltra')}
          </MinimizedUltraAlert>
        )}

        {activeAlert === 'newChatTip' && (
          <MinimizedTipAlert onClick={handleTipClick}>
            {LightbulbIcon && <LightbulbIcon size={16} />}
            {isMobile
              ? t('chat.input.labels.startNewChat')
              : t('chat.input.labels.startNewChatLong')}
          </MinimizedTipAlert>
        )}

        <OptimizedInput
          ref={optimizedInputRef}
          onSendMessage={checkAndSendMessage}
          isWaitingForResponse={isWaitingForResponse}
          isStreaming={isStreaming}
          onStopStreaming={onStopStreaming}
          rateLimitInfo={rateLimitInfo}
          onOpenAGTPromo={onOpenAGTPromo}
          selectedModel={selectedModel}
          onModelChange={onModelChange}
          selectedPersonality={selectedPersonality}
          onPersonalityChange={onPersonalityChange}
          isPro={isPro}
          userId={userId}
          chatId={chatId}
          error={combinedError}
          onErrorClose={handleLocalErrorClose}
          lastAssistantMessage={lastAssistantMessage}
          onPromptAction={onPromptAction}
          onProjectClick={onProjectClick}
          onRecordingStateChange={setIsAudioRecording}
        />
      </InputWrapper>
    </MessageBarContainer>
  );
});

// Agregar displayName para depuración
ChatInput.displayName = 'ChatInput';

export default React.memo(ChatInput);